/**
 * src/lib/auth/cachedSession.js — offline session + profile hydration.
 *
 * Why this exists (CEO report 2026-06-11, airplane-mode screenshot showing
 * "Sign in to continue" after a full refresh in flight):
 *
 * An installed iOS PWA that gets suspended in the background is COLD-RELOADED by
 * iOS when reopened. On that reload supabase.auth.getSession() finds the access
 * token already expired — the Supabase default JWT lifetime (1h) is shorter than
 * a flight — so it tries to REFRESH it. Offline that refresh fails and
 * getSession() returns a NULL session, so AuthGate renders <Login>. But you
 * cannot sign in to Supabase with no network, so the login screen is a dead end
 * on a plane. The app is otherwise fully offline-capable (reads come from
 * IndexedDB / Cache Storage, edits queue in the outbox), so it should open to
 * cached data, not bounce to auth.
 *
 * Fix: when getSession() yields nothing AND we are offline, fall back to the
 * session Supabase already persisted in localStorage (under the auth storage
 * key) plus a cached copy of the user_profiles row. The stored access token may
 * be stale, but no offline read touches the network, so a stale token is
 * harmless until connectivity returns — at which point supabase-js refreshes it
 * (or genuinely signs the user out) on its own.
 *
 * Storage keys are intentionally duplicated as string literals (not imported
 * from src/lib/supabase) so this module never pulls the Supabase client — it
 * must stay a pure, side-effect-free localStorage reader.
 */

// MUST match createClient({ auth: { storageKey } }) in src/lib/supabase.js.
const AUTH_STORAGE_KEY = 'acn-auth';
const PROFILE_CACHE_KEY = 'acn-profile-cache';

/**
 * Read the Supabase session persisted in localStorage WITHOUT touching the
 * network. supabase-js v2 stores JSON.stringify(session) directly under the
 * storage key; older shapes wrapped it as { currentSession }. Tolerate both.
 * @returns {{ user: object, expires_at?: number }|null}
 */
export function readStoredSession() {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const session = parsed?.currentSession ?? parsed; // tolerate legacy wrapper
    if (session && typeof session === 'object' && session.user?.id) return session;
    return null;
  } catch {
    return null;
  }
}

/**
 * Persist the user_profiles row so role/display_name survive an offline
 * cold-boot. Best-effort — quota errors / private mode never throw.
 * @param {object|null} profile
 */
export function cacheProfile(profile) {
  if (typeof localStorage === 'undefined' || !profile?.id) return;
  try {
    localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(profile));
  } catch {
    /* storage full / private mode — the cache is an optimisation, not a guarantee */
  }
}

/**
 * Read the cached user_profiles row. When userId is given, the cache is only
 * returned if it belongs to that user — so a stale cache from a previously
 * signed-in account is never used to render the wrong role offline.
 * @param {string} [userId]
 * @returns {object|null}
 */
export function readCachedProfile(userId) {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(PROFILE_CACHE_KEY);
    if (!raw) return null;
    const profile = JSON.parse(raw);
    if (!profile?.id) return null;
    if (userId && profile.id !== userId) return null;
    return profile;
  } catch {
    return null;
  }
}

/** Drop the cached profile (call on sign-out so the next account starts clean). */
export function clearCachedProfile() {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(PROFILE_CACHE_KEY);
  } catch {
    /* best-effort */
  }
}
